// packages/ui/src/components/Toast.tsx
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, useCallback } from 'react'

import { cn } from '../utils/cn'

// ─── Types ───
export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface ToastOptions {
  type: ToastType
  message: string
  duration?: number // ms, default 4000
}

interface ToastItem extends ToastOptions {
  id: string
}

// ─── Event Bus singleton ───
type Listener = (toast: ToastItem) => void
const listeners = new Set<Listener>()
let idCounter = 0

/**
 * Affiche un toast global. Appeler depuis n'importe où (hooks, services, composants).
 * Le `<ToastContainer />` doit être monté dans l'app pour afficher les toasts.
 *
 * @example
 * toast({ type: 'success', message: 'Prompt copié !' })
 * toast({ type: 'error', message: 'Erreur réseau', duration: 6000 })
 */
export function toast(options: ToastOptions): void {
  const item: ToastItem = {
    ...options,
    id: `toast-${++idCounter}-${Date.now()}`,
    duration: options.duration ?? 4000,
  }
  listeners.forEach((fn) => fn(item))
}

// ─── Style mapping ───
const TOAST_STYLES: Record<ToastType, { icon: string; bg: string; border: string; text: string }> =
  {
    success: {
      icon: '✅',
      bg: 'bg-green-500/10 dark:bg-green-500/15',
      border: 'border-green-500/30',
      text: 'text-green-600 dark:text-green-400',
    },
    error: {
      icon: '❌',
      bg: 'bg-red-500/10 dark:bg-red-500/15',
      border: 'border-red-500/30',
      text: 'text-red-600 dark:text-red-400',
    },
    warning: {
      icon: '⚠️',
      bg: 'bg-amber-500/10 dark:bg-amber-500/15',
      border: 'border-amber-500/30',
      text: 'text-amber-600 dark:text-amber-400',
    },
    info: {
      icon: 'ℹ️',
      bg: 'bg-indigo-500/10 dark:bg-indigo-500/15',
      border: 'border-indigo-500/30',
      text: 'text-indigo-600 dark:text-indigo-400',
    },
  }

// ─── ToastContainer (monter UNE SEULE FOIS dans l'app) ───
export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  // Subscribe to event bus
  useEffect(() => {
    const handler: Listener = (item) => {
      setToasts((prev) => [...prev, item])
    }
    listeners.add(handler)
    return () => {
      listeners.delete(handler)
    }
  }, [])

  // Auto-dismiss
  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  useEffect(() => {
    if (toasts.length === 0) return
    const latest = toasts[toasts.length - 1]
    const timer = setTimeout(() => dismiss(latest.id), latest.duration ?? 4000)
    return () => clearTimeout(timer)
  }, [toasts, dismiss])

  return (
    <div
      className="pointer-events-none fixed right-4 top-4 z-[9999] flex flex-col gap-2"
      aria-live="polite"
      aria-label="Notifications"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((t) => {
          const style = TOAST_STYLES[t.type]
          return (
            <motion.div
              key={t.id}
              layout
              initial={Object.assign({}, { opacity: 0, x: 80, scale: 0.95 })}
              animate={Object.assign({}, { opacity: 1, x: 0, scale: 1 })}
              exit={Object.assign({}, { opacity: 0, x: 80, scale: 0.95 })}
              transition={Object.assign(
                {},
                { type: 'spring' as const, stiffness: 400, damping: 30 },
              )}
              className={cn(
                'pointer-events-auto flex items-center gap-3 px-4 py-3',
                'rounded-2xl border shadow-lg backdrop-blur-[16px]',
                'min-w-[280px] max-w-[420px] cursor-pointer',
                style.bg,
                style.border,
              )}
              onClick={() => dismiss(t.id)}
              role="alert"
            >
              <span className="shrink-0 text-base">{style.icon}</span>
              <p className={cn('flex-1 text-sm font-semibold', style.text)}>{t.message}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  dismiss(t.id)
                }}
                className="shrink-0 text-xs text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200"
                aria-label="Fermer"
              >
                ✕
              </button>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}
